import { can, formatDate } from '@clim/client-core';
import { SAV_STATUS_LABELS, type DossierRecord, type SavRecord, type SavStatus } from '@clim/domain';
import { useState } from 'react';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { InterventionCard, InterventionDialog } from './Interventions';
import { ErrorBox, Field, Modal, Textarea } from './ui';

export const SAV_TONE: Record<SavStatus, string> = {
  pending_validation: 'warning',
  open: 'warning',
  in_progress: 'progress',
  done: 'info',
  closed: 'success',
  rejected: 'neutral',
  cancelled: 'neutral',
};

export function SavBadge({ s }: { s: SavRecord }) {
  return <span className={`badge tone-${SAV_TONE[s.status]}`}>{SAV_STATUS_LABELS[s.status]}</span>;
}

/** Choix dans une liste administrable (motifs / causes) + commentaire. */
function ReasonPick({ category, value, onChange }: { category: string; value: string; onChange(id: string): void }) {
  const engine = useEngine();
  const reasons = engine
    .list('reason')
    .filter((r) => r.category === category && r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <div className="chips" role="radiogroup" aria-label={category === 'sav_cause' ? 'Cause' : 'Motif'}>
      {reasons.map((r) => (
        <button key={r.id} type="button" role="radio" aria-checked={value === r.id} className={`chip${value === r.id ? ' on' : ''}`} onClick={() => onChange(r.id)}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

/** SAV du dossier (§12) : déclaration depuis une installation terminée, validation, planification, clôture. */
export function DossierSav({ dossier }: { dossier: DossierRecord }) {
  const engine = useEngine();
  const { me } = useReady();
  const [creating, setCreating] = useState(false);
  const [openIv, setOpenIv] = useState<string | null>(null);
  const [act, setAct] = useState<{ sav: SavRecord; action: 'reject' | 'close' | 'cancel' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savs = engine.savsOf(dossier.id);
  const canCreate = can(me, 'sav.create') && dossier.status === 'completed';
  if (!savs.length && !canCreate) return null;
  const reasonLabel = (id: string | null) => (id ? (engine.get('reason', id)?.label ?? '—') : '—');
  const run = (p: Promise<unknown>) => {
    setError(null);
    return p.catch((e) => setError(errorMessage(e)));
  };
  return (
    <div className="card">
      <div className="card-head">
        <h2>SAV</h2>
        {canCreate && (
          <button className="btn small primary" onClick={() => setCreating(true)}>
            + Déclarer un SAV
          </button>
        )}
      </div>
      {!savs.length && <p className="empty">Aucun SAV sur cette installation.</p>}
      {savs.map((s) => {
        const iv = s.interventionId ? engine.get('intervention', s.interventionId) : undefined;
        const validator = can(me, 'sav.validate');
        return (
          <div key={s.id} className="sav-item">
            <div className="row-main">
              <strong>
                {s.reference ?? 'SAV'} · {reasonLabel(s.reasonId)} <SavBadge s={s} />
                {engine.syncState('sav', s.id) !== 'synced' && <span className="sync-tag"> ⟳</span>}
              </strong>
              <span className="muted">
                déclaré par {s.createdByName} le {formatDate(s.createdAt)}
                {s.installerName ? ` · installation réalisée par ${s.installerName}` : ''}
              </span>
              {s.description && <div className="pre">{s.description}</div>}
              {s.statusComment && <div className="muted">Motif : {s.statusComment}</div>}
              {s.status === 'closed' && (
                <div>
                  Clôturé par {s.closedByName} — cause : <strong>{reasonLabel(s.causeId)}</strong>
                  {s.closingComment ? ` — ${s.closingComment}` : ''}
                </div>
              )}
            </div>
            {iv && <InterventionCard i={iv} onOpen={() => setOpenIv(iv.id)} />}
            <div className="actions wrap">
              {validator && s.status === 'pending_validation' && (
                <>
                  <button className="btn small primary" onClick={() => void run(engine.transitionSav(s.id, 'validate'))}>
                    Valider le SAV
                  </button>
                  <button className="btn small" onClick={() => setAct({ sav: s, action: 'reject' })}>
                    Refuser
                  </button>
                </>
              )}
              {can(me, 'planning.manage') && (s.status === 'open' || (s.status === 'in_progress' && iv && ['impossible', 'cancelled'].includes(iv.status))) && (
                <button className="btn small primary" onClick={() => void run(engine.createIntervention(dossier.id, { savId: s.id }).then(setOpenIv))}>
                  Planifier l’intervention SAV
                </button>
              )}
              {validator && ['open', 'in_progress', 'done'].includes(s.status) && (
                <button className={`btn small${s.status === 'done' ? ' primary' : ''}`} onClick={() => setAct({ sav: s, action: 'close' })}>
                  Clôturer
                </button>
              )}
              {validator && ['pending_validation', 'open'].includes(s.status) && (
                <button className="btn small danger-outline" onClick={() => setAct({ sav: s, action: 'cancel' })}>
                  Annuler
                </button>
              )}
            </div>
          </div>
        );
      })}
      <ErrorBox message={error} />
      {creating && <CreateSav dossier={dossier} onClose={() => setCreating(false)} />}
      {act && <SavActionDialog sav={act.sav} action={act.action} onClose={() => setAct(null)} />}
      {openIv && <InterventionDialog id={openIv} onClose={() => setOpenIv(null)} />}
    </div>
  );
}

function CreateSav({ dossier, onClose }: { dossier: DossierRecord; onClose(): void }) {
  const engine = useEngine();
  const { me } = useReady();
  const [reasonId, setReasonId] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const r = reasonId ? engine.get('reason', reasonId) : undefined;
  return (
    <Modal title="Déclarer un SAV" onClose={onClose}>
      <div className="form">
        <ReasonPick category="sav_reason" value={reasonId} onChange={setReasonId} />
        <Field label={`Commentaire${r?.requiresComment ? ' (obligatoire)' : ''}`}>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ce que constate le client…" />
        </Field>
        <p className="muted">Photos : ajoutez-les dans « Photos et documents » du dossier, catégorie « SAV ».</p>
        {!can(me, 'sav.validate') && <p className="muted">Le SAV sera validé par un responsable avant d’être planifié.</p>}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={!reasonId || (!!r?.requiresComment && !description.trim())}
            onClick={() =>
              engine
                .createSav(dossier.id, { reasonId, description })
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Déclarer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SavActionDialog({ sav, action, onClose }: { sav: SavRecord; action: 'reject' | 'close' | 'cancel'; onClose(): void }) {
  const engine = useEngine();
  const [causeId, setCauseId] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const title = action === 'close' ? 'Clôturer le SAV' : action === 'reject' ? 'Refuser le SAV' : 'Annuler le SAV';
  const cause = causeId ? engine.get('reason', causeId) : undefined;
  const ok = action === 'close' ? !!causeId && (!cause?.requiresComment || !!comment.trim()) : comment.trim().length >= 3;
  return (
    <Modal title={title} onClose={onClose}>
      <div className="form">
        {action === 'close' && (
          <>
            <p className="muted">Cause du SAV :</p>
            <ReasonPick category="sav_cause" value={causeId} onChange={setCauseId} />
          </>
        )}
        <Field label={`Commentaire${action !== 'close' || cause?.requiresComment ? ' (obligatoire)' : ''}`}>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={!ok}
            onClick={() =>
              engine
                .transitionSav(sav.id, action, { causeId: action === 'close' ? causeId : null, comment })
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Confirmer
          </button>
        </div>
      </div>
    </Modal>
  );
}
